import { Button, Tabs } from 'antd'
import { LeftOutlined } from '@ant-design/icons'
import { useNavigate, useSearchParams } from 'react-router-dom'
import FeatureThemeProvider from '@/components/FeatureThemeProvider'
import { useAuthSession } from '@/lib/session'
import { UserTypes } from '@/types/authorization'
import { AvatarVoicePanel } from './components/AvatarVoicePanel'
import { AgendaTemplatesPanel } from './components/AgendaTemplatesPanel'
import { ThemeColorsPanel } from './components/ThemeColorsPanel'
import { isSettingsTab } from './tabs'

export default function AdvisoryAiSettingsPage() {
  const session = useAuthSession()
  const navigate = useNavigate()
  const clientId = session?.currentClientId || ''
  /**
   * The URL is the tab, rather than state seeded from it. A pop-up can deep-link
   * here (see popupTargetRoute) while this page is already open — the search
   * param changes without remounting, so state seeded once would never move.
   */
  const [searchParams, setSearchParams] = useSearchParams()
  const requestedTab = searchParams.get('tab')
  // Fails closed: an unrecognised user type normalises to undefined, which must
  // not read as "not a client user".
  const userTypeId = session?.currentUserTypeId
  const canViewThemeColors = userTypeId !== undefined && userTypeId !== UserTypes.ClientUser
  const resolvedTab = isSettingsTab(requestedTab) ? requestedTab : 'avatarVoice'
  const activeTab = resolvedTab === 'theme' && !canViewThemeColors ? 'avatarVoice' : resolvedTab
  const setActiveTab = (tab: string) =>
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev)
        next.set('tab', tab)
        return next
      },
      { replace: true }
    )

  return (
    <FeatureThemeProvider featureKey="advisoryIntel" withAntd className="w-full space-y-4">
      <div className="flex justify-between items-center">
        <div className="flex items-center gap-2">
          <Button icon={<LeftOutlined />} onClick={() => navigate(-1)}>
            Back
          </Button>
          <h1 className="font-display text-2xl font-bold text-gray-900">AdvicePrep Settings</h1>
        </div>
      </div>

      <Tabs
        activeKey={activeTab}
        onChange={setActiveTab}
        items={[
          {
            key: 'avatarVoice',
            label: 'Avatar & Voice',
            children: <AvatarVoicePanel />,
          },
          {
            key: 'templates',
            label: 'Agenda Templates',
            children: <AgendaTemplatesPanel clientId={clientId} />,
          },
          ...(canViewThemeColors
            ? [
                {
                  key: 'theme',
                  label: 'Theme Colours',
                  children: <ThemeColorsPanel clientId={clientId} />,
                },
              ]
            : []),
        ]}
      />
    </FeatureThemeProvider>
  )
}
